import React from 'react';
import wrap from 'wraptile';
import {onGoToNextPrevious} from 'VideoAudioQC/Timeline/TimelineUtils';
import {SetActiveTab} from 'VideoAudioQC/QCTabs/Redux/QCTabsActions';
import {TabIndex} from 'VideoAudioQC/QCTabs/QCTabsTypes';
import {batch} from 'react-redux';
import {curry} from 'ramda';
import {
    SetShowHotkeys,
    SetShowSafeMargins,
} from 'VideoAudioQC/AccuratePlayer/Redux/AccuratePlayerActions';
import {SelectNextPreviousMarker} from '../Timeline/Redux/TimelineActions';
import PlayerControls from './PlayerControls';
import {PlaybackSpeedChange} from '../VideoAudioQCTypes';

export const onShowHotkeys = wrap((showHotkeys: SetShowHotkeys) => {
    showHotkeys(true);
});

export interface PlayerControlsContainerProps {
    paused: boolean;
    onStepOneFrameBack: () => void;
    onStepOneFrameForward: () => void;
    onPlay: () => void;
    onPause: () => void;
    onFiveSecondsForward: () => void;
    onFiveSecondsBack: () => void;
    onVolumeChange: () => void;
    onToggleMute: () => void;
    volume: number;
    muted: boolean;
    onFullscreen: () => void;
    onSetIn: () => void;
    onSetOut: () => void;
    onClearPoints: () => void;
    onCreateQCNote: (rowId: string) => void;
    onCreateAdBreak: (rowId: string) => void;
    onCreateMarkup: (rowId: string) => void;
    onGoToIn: () => void;
    onGoToOut: () => void;
    setActiveTab: SetActiveTab;
    activeTab: number;
    qcReportRowId: string;
    adBreaksRowId: string;
    markupRowId: string;
    selectNextPreviousMarker: SelectNextPreviousMarker;
    setShowHotkeys: SetShowHotkeys;
    showSafeMargins: boolean;
    setShowSafeMargins: SetShowSafeMargins;
    jobDeliveryDate: string;
    playbackSpeed: number;
    onPlaybackSpeedChange: PlaybackSpeedChange;
    playbackOptions: number[];
    onGetSnapshot: () => void;
}

export const getMuteButtonDisabled = (volume: number): boolean => {
    return volume === 0;
};

export const getCurrentVolume = (muted: boolean, volume: number): number => {
    return muted ? 0 : volume;
};

export interface HandleCreateMarkerProps {
    onCreateMarker: (rowId: string) => void;
    setActiveTab: SetActiveTab;
    rowId: string;
    activeTab: TabIndex;
    markerToCreateInTab: TabIndex;
}

export const dispatchCreateMarkerActions = wrap((props: HandleCreateMarkerProps) => {
    const {onCreateMarker, setActiveTab, rowId, activeTab, markerToCreateInTab} = props;

    if (activeTab !== markerToCreateInTab) setActiveTab(markerToCreateInTab);

    onCreateMarker(rowId);
});

export const handleOnCreateMarker = wrap((props: HandleCreateMarkerProps) => {
    batch(dispatchCreateMarkerActions(props));
});

type OnSetInOut = () => void;

export const handleOnSetInOut = wrap((onSetInOut: OnSetInOut) => {
    onSetInOut();
});

export const onSafeMarginsChange = curry(
    (
        setShowSafeMargins: SetShowSafeMargins,
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        setShowSafeMargins(checked);
    }
);

function PlayerControlsContainer(props: PlayerControlsContainerProps) {
    const {
        paused,
        onStepOneFrameForward,
        onStepOneFrameBack,
        onPlay,
        onPause,
        onFiveSecondsForward,
        onFiveSecondsBack,
        onVolumeChange,
        onToggleMute,
        volume,
        muted,
        onFullscreen,
        onSetIn,
        onSetOut,
        onClearPoints,
        onCreateQCNote,
        onCreateAdBreak,
        onCreateMarkup,
        onGoToIn,
        onGoToOut,
        setActiveTab,
        activeTab,
        qcReportRowId,
        adBreaksRowId,
        markupRowId,
        selectNextPreviousMarker,
        setShowHotkeys,
        showSafeMargins,
        setShowSafeMargins,
        jobDeliveryDate,
        playbackSpeed,
        onPlaybackSpeedChange,
        playbackOptions,
        onGetSnapshot,
    } = props;

    const currentVolume = getCurrentVolume(muted, volume);
    const muteButtonDisabled = getMuteButtonDisabled(volume);
    const handleCreateQCNote = handleOnCreateMarker({
        onCreateMarker: onCreateQCNote,
        setActiveTab,
        activeTab,
        rowId: qcReportRowId,
        markerToCreateInTab: TabIndex.QCReport,
    });
    const handleCreateAdBreak = handleOnCreateMarker({
        onCreateMarker: onCreateAdBreak,
        setActiveTab,
        activeTab,
        rowId: adBreaksRowId,
        markerToCreateInTab: TabIndex.AdBreaks,
    });
    const handleCreateMarkup = handleOnCreateMarker({
        onCreateMarker: onCreateMarkup,
        setActiveTab,
        activeTab,
        rowId: markupRowId,
        markerToCreateInTab: TabIndex.Markup,
    });
    const setIn = handleOnSetInOut(onSetIn);
    const setOut = handleOnSetInOut(onSetOut);
    const handleNextMarker = onGoToNextPrevious({
        next: true,
        selectNextPreviousMarker,
    });
    const handlePreviousMarker = onGoToNextPrevious({
        next: false,
        selectNextPreviousMarker,
    });
    const handleShowHotkeys = onShowHotkeys(setShowHotkeys);
    const handleSafeMarginsChange = onSafeMarginsChange(setShowSafeMargins);

    return (
        <PlayerControls
            paused={paused}
            onGetSnapshot={onGetSnapshot}
            jobDeliveryDate={jobDeliveryDate}
            onStepOneFrameBack={onStepOneFrameBack}
            onStepOneFrameForward={onStepOneFrameForward}
            onPlay={onPlay}
            onPause={onPause}
            onFiveSecondsForward={onFiveSecondsForward}
            onFiveSecondsBack={onFiveSecondsBack}
            onVolumeChange={onVolumeChange}
            volume={currentVolume}
            onToggleMute={onToggleMute}
            muteButtonDisabled={muteButtonDisabled}
            muted={muted}
            onFullscreen={onFullscreen}
            onSetIn={setIn}
            onSetOut={setOut}
            onClearPoints={onClearPoints}
            onCreateQCNote={handleCreateQCNote}
            onCreateAdBreak={handleCreateAdBreak}
            onCreateMarkup={handleCreateMarkup}
            onGoToIn={onGoToIn}
            onGoToOut={onGoToOut}
            onNextMarker={handleNextMarker}
            onPreviousMarker={handlePreviousMarker}
            onOpenHotkeys={handleShowHotkeys}
            showSafeMargins={showSafeMargins}
            onSafeMarginsChange={handleSafeMarginsChange}
            playbackSpeed={playbackSpeed}
            playbackSpeedOptions={playbackOptions}
            onPlaybackSpeedChange={onPlaybackSpeedChange}
        />
    );
}

export default PlayerControlsContainer;
